<template>
  <div class="container">
    <Breadcrumbs :items="breadcrumbs" />
    <div class="py-20 lg:py-32">
      <h1 class="section-headline text-center text-black-light mb-20 lg:mb-32">
        Informativa sul trattamento e la protezione dei dati personali
      </h1>
      <ol class="list-decimal">
        <li class="mb-12 marker:font-bold">
          <p class="paragraph mb-6">
            Titolare del trattamento e responsabile della protezione dei dati
          </p>
          <p class="mb-6">
            Il <strong>Titolare del Trattamento</strong> è
            <strong>UniCredit RE Services S.p.A.</strong> con sede legale presso
            {{ $config.brandAddress.full }} (UniCredit RE Services).
          </p>
          <p class="mb-6">
            Il <strong>Responsabile della protezione dei dati</strong> può
            essere contattato presso <strong>UniCredit S.p.A.</strong>, Data
            Protection Office, Piazza Gae Aulenti n.3, Tower A, 20154 Milano,
            E-mail:
            <a
              class="text-blue underline hover:no-underline"
              target="_blank"
              href="mailto:Group.DPO@unicredit.eu"
              >Group.DPO@unicredit.eu</a
            >, PEC:
            <a
              class="text-blue underline hover:no-underline"
              target="_blank"
              href="mailto:Group.DPO@pec.unicredit.eu"
              >Group.DPO@pec.unicredit.eu</a
            >.
          </p>
        </li>
        <li class="mb-12 marker:font-bold">
          <p class="paragraph mb-6">
            Finalità e base giuridica del trattamento
          </p>
          <p class="mb-6">
            UniCredit RE Services tratta i dati personali per le seguenti
            finalità:
          </p>
          <ul class="list-[upper-alpha] ml-12">
            <li class="mb-6 marker:font-normal">
              Necessità di
              <strong>eseguire un contratto di cui Lei sia parte</strong>
              o di
              <strong>eseguire attività precontrattuali su Sua richiesta</strong
              >. Tale necessità rappresenta la base giuridica che legittima i
              conseguenti trattamenti. Il conferimento dei dati necessari a tali
              fini rappresenta, a seconda dei casi, un obbligo contrattuale o un
              requisito necessario alla conclusione del contratto; in mancanza
              di essi, UniCredit RE Services S.p.A. potrebbe essere
              nell'impossibilità di instaurare il rapporto o di dare esecuzione
              allo stesso.
            </li>
            <li class="mb-6 marker:font-normal">
              Necessità di adempiere ad
              <strong>obblighi legali </strong>(es. obblighi previsti dalla
              normativa antiriciclaggio, disposizioni impartite da Autorità o
              dalla Magistratura, ecc.). Tale necessità rappresenta la base
              giuridica che legittima i conseguenti trattamenti. Il conferimento
              dei dati necessari a tali fini rappresenta un obbligo legale; in
              mancanza di essi UniCredit RE Services S.p.A. sarebbe
              nell'impossibilità di instaurare rapporti e potrebbe avere
              l'obbligo di effettuare segnalazioni.
            </li>
          </ul>
        </li>
        <li class="mb-12 marker:font-bold">
          <p class="paragraph mb-6">
            trattamento dei dati personali sulla base giuridica del legittimo
            interesse
          </p>
          <p class="mb-6">
            UniCredit RE Services, inoltre, tratta i Suoi dati personali sulla
            base del legittimo interesse per le seguenti finalità:
          </p>
          <ol class="list-decimal ml-12">
            <li class="mb-6 marker:font-normal">
              svolgere attività funzionali e strettamente connesse ad eventuali
              operazioni di <strong>cartolarizzazione</strong>,
              <strong>cessioni del credito</strong> e/o
              <strong>operazioni societarie</strong> (quali fusioni e/o
              acquisizioni);
            </li>
            <li class="mb-6 marker:font-normal">
              effettuare attività di controllo finalizzate a
              <strong
                >prevenire e/o reprimere azioni fraudolente e/o dannose</strong
              >, anche di natura informatica, e a gestire i propri
              <strong>sistemi informatici</strong>, ivi incluse le
              infrastrutture e i siti web per assicurare la continuità operativa
              (c.d. <em>business continuity</em>) e, al contempo, per garantire
              la <strong>sicurezza informatica</strong> ed eseguire test,
              aggiornamenti ed altre attività di sviluppo e manutenzione, anche
              attraverso statistiche aggregate e analisi di dati non
              identificabili, volte all'ottimizzazione dei prodotti e/o servizi
              di UniCredit RE Services, ivi inclusi i propri sistemi
              informatici, applicazioni e funzionalità collegate, utilizzando la
              quantità minima di dati personali, solo se necessari, per
              garantire l'affidabilità e correttezza di tali attività, e
              provvedendo alla cancellazione e/o anonimizzazione dei risultati,
              salvo nel caso in cui sussista altra base legale per conservarli.
            </li>
          </ol>
          <p class="mb-6">
            Il trattamento dei dati personali basato sul legittimo interesse non
            è obbligatorio e Lei potrà opporsi a detto trattamento in qualsiasi
            momento, per motivi connessi alla Sua situazione particolare, con le
            modalità indicate al successivo paragrafo 9 e, salvo sussistano
            motivi legittimi cogenti prevalenti e/o di esercizio e/o difesa di
            un diritto di UniCredit RE Services o di terzi, UniCredit RE
            Services si asterrà dal trattare ulteriormente i dati.
          </p>
        </li>

        <li class="mb-12 marker:font-bold">
          <p class="paragraph mb-6">categorie di dati trattati</p>
          <p class="mb-6">
            UniCredit RE Services tratta dati personali raccolti direttamente
            presso di Lei, ovvero presso terzi, che includono, a titolo
            esemplificativo,
            <strong>dati anagrafici </strong>(es. nome, cognome, indirizzo, data
            e luogo di nascita),
            <strong
              >informazioni per la gestione dei servizi immobiliari e degli
              incarichi di mediazione immobiliare</strong
            >
            (es. estremi conto corrente bancario per pagamento provvigioni),
            <strong>dati relativi all'immagine </strong>(es. foto su carta
            d'identità) e altri dati riconducibili alle categorie sopra
            indicate.
          </p>
        </li>
        <li class="mb-12 marker:font-bold">
          <p class="paragraph mb-6">
            destinatari o categorie di destinatari dei dati personali
          </p>
          <p class="mb-6">
            Possono venire a conoscenza dei Suoi dati in qualità di
            <strong>Responsabili del trattamento</strong> e/o
            <strong>Titolari autonomi del trattamento</strong> le persone
            fisiche e giuridiche di cui all'elenco consultabile presso sul sito
            <a
              class="text-blue underline hover:no-underline"
              target="_blank"
              :href="`${$config.baseURL}`"
              >www.unicreditres.it</a
            >, e in qualità di
            <strong
              >persone autorizzate al trattamento dei dati personali</strong
            >, relativamente ai dati necessari allo svolgimento delle mansioni
            assegnategli, le persone fisiche appartenenti alle seguenti
            categorie: i lavoratori dipendenti di UniCredit RE Services o presso
            di essa distaccati, i lavoratori interinali, gli stagisti, i
            consulenti e i dipendenti delle società esterne nominate
            Responsabili.
          </p>
          <p class="mb-6">I dati possono essere comunicati:</p>
          <ul class="list-[upper-roman] ml-12">
            <li class="mb-6 marker:font-normal">
              a quei soggetti cui tale comunicazione debba essere effettuata in
              adempimento di un obbligo previsto dalla legge, da un regolamento
              o dalla normativa comunitaria. Maggiori informazioni al riguardo
              sono rinvenibili nell'Informativa per i clienti pubblicata nella
              sezione “Privacy” del sito web
              <a
                class="text-blue underline hover:no-underline"
                target="_blank"
                :href="`${$config.baseURL}`"
                >www.unicreditres.it;</a
              >
            </li>
            <li class="mb-6 marker:font-normal">
              agli intermediari finanziari appartenenti al Gruppo UniCredit, in
              base a quanto disposto dalla normativa antiriciclaggio (cfr. Art.
              39, comma 3, del D. Lgs. n. 90/2017), che prevede la possibilità
              di procedere alla comunicazione dei dati personali relativi alle
              segnalazioni considerate sospette tra gli intermediari finanziari
              facenti parte del medesimo Gruppo UniCredit;
            </li>
            <li class="mb-6 marker:font-normal">
              alle società appartenenti al Gruppo UniCredit, ovvero controllate
              o collegate ai sensi dell'art. 2359 C.C. (situate anche
              all'estero), quando tale comunicazione sia consentita in
              conseguenza di un provvedimento del Garante della Privacy o di una
              previsione di legge.
            </li>
          </ul>
          <p class="mb-6">
            L'elenco dettagliato dei soggetti ai quali i dati possono essere
            comunicati può essere consultato nella sezione “Privacy” del sito
            web
            <a
              class="text-blue underline hover:no-underline"
              target="_blank"
              :href="`${$config.baseURL}`"
              >www.unicreditres.it</a
            >.
          </p>
        </li>

        <li class="mb-12 marker:font-bold">
          <p class="paragraph mb-6">trasferimento dei dati verso paesi terzi</p>
          <p class="mb-6">
            UniCredit RE Services informa che i dati personali potranno essere
            traferiti anche in paesi non appartenenti all'Unione Europea o allo
            Spazio Economico Europeo (cd. Paesi Terzi) riconosciuti dalla
            Commissione Europea aventi un livello adeguato di protezione dei
            dati personali o, in caso contrario, solo se sia garantito
            contrattualmente da tutti i fornitori di UniCredit RE Services
            situati nel Paese Terzo un livello di protezione dei dati personali
            adeguato rispetto a quello dell'Unione Europea (es. tramite la
            sottoscrizione delle clausole contrattuali standard previsti dalla
            Commissione Europea) e che sia sempre assicurato l'esercizio dei
            diritti degli Interessati. Ulteriori informazioni possono essere
            richieste scrivendo a
            <a
              class="text-blue underline hover:no-underline"
              target="_blank"
              href="mailto:Group.DPO@unicredit.eu"
              >Group.DPO@unicredit.eu</a
            >
          </p>
        </li>
        <li class="mb-12 marker:font-bold">
          <p class="paragraph mb-6">diritti degli interessati</p>
          <p class="mb-6">
            Il Regolamento generale sulla protezione dei dati (“Regolamento UE
            679/2016”) attribuisce alle persone fisiche, ditte individuali e/o
            liberi professionisti (“<strong>Interessati</strong>”) specifici
            diritti, tra i quali quello di conoscere quali sono i dati personali
            in possesso di UniCredit RE Services e come questi vengono
            utilizzati (<strong>Diritto di accesso</strong>), di ottenerne
            l'<strong>aggiornamento</strong>, la <strong>rettifica</strong> o,
            se vi è interesse, l'<strong>integrazione</strong>, nonché la
            <strong>cancellazione</strong>, la trasformazione in
            <strong>forma anonima</strong> o la <strong>limitazione</strong>.
          </p>
          <p class="mb-6">
            Ciascun Interessato può chiedere di ricevere o chiedere il
            trasferimento dei dati personali a lui riferibili in possesso di
            UniCredit RE Services in un formato strutturato, di uso comune e
            leggibile per ulteriori usi personali ovvero per fornirli ad altro
            titolare del trattamento (<strong>Diritto alla portabilità</strong
            >).
          </p>
          <p class="mb-6">
            In particolare, i dati che possono essere oggetto di portabilità
            sono i dati anagrafici (es. nome, cognome, titolo, data nascita,
            sesso, luogo nascita, residenza, ecc.), nonché un set di dati
            generati dall'attività di mediazione immobiliare che UniCredit RE
            Services S.p.A. ha definito per ciascuna macro-categoria di
            prodotti/servizi (es. rapporti in essere o estinti, movimentazione).
          </p>
        </li>

        <li class="mb-12 marker:font-bold">
          <p class="paragraph mb-6">
            periodo di conservazione dei dati e diritto alla cancellazione (i.e.
            diritto all'oblio)
          </p>
          <p class="mb-6">
            UniCredit RE Services S.p.A. tratta e conserva i Suoi dati personali
            per tutta la durata del rapporto contrattuale, per l'esecuzione
            degli adempimenti allo stesso inerenti e conseguenti, per il
            rispetto degli obblighi di legge e regolamentari applicabili, nonché
            per finalità difensive proprie o di terzi e fino alla scadenza del
            periodo di prescrizione di legge applicabile, decorrente dalla data
            di chiusura del rapporto contrattuale relativo al singolo
            prodotto/servizio.
          </p>
          <p class="mb-6">
            <strong>Al termine del periodo di conservazione applicabile</strong
            >, i dati personali riferibili agli Interessati verranno cancellati
            o
            <strong
              >conservati in una forma che non consenta l'identificazione </strong
            >dell'Interessato (es. anonimizzazione irreversibile), a meno che il
            loro ulteriore trattamento sia necessario per uno o più dei seguenti
            scopi: i) risoluzione di
            <strong>precontenziosi e/o contenziosi </strong>avviati prima della
            scadenza del periodo di conservazione; ii) per dare seguito a
            <strong>indagini/ispezioni</strong> da parte di funzioni di
            controllo interno e/o autorità esterne avviati prima della scadenza
            del periodo di conservazione; iii) per dare seguito a richieste
            della
            <strong>pubblica autorità italiana e/o estera</strong>
            pervenute/notificate a UniCredit RE Services S.p.A. prima della
            scadenza del periodo di conservazione.
          </p>
        </li>
        <li class="mb-12 marker:font-bold">
          <p class="paragraph mb-6">modalità di esercizio dei diritti</p>
          <p class="mb-6">
            Ciascun Interessato per esercitare i diritti di cui al paragrafo 8 e
            9 potrà rivolgersi a: UniCredit RE Services S.p.A., Legale &
            Compliance,
            {{ $config.brandAddress.full }}, fax +39 0297688500, indirizzo
            e-mail:
            <a
              class="text-blue underline hover:no-underline"
              target="_blank"
              href="mailto:privacy@unicreditreservices.it"
              >privacy@unicreditreservices.it</a
            >.
          </p>
          <p class="mb-6">
            Il termine per la risposta è <strong>un (1) mese</strong>,
            prorogabile di <strong>due (2) mesi</strong> in casi di particolare
            complessità; in questi casi, UniCredit RE Services S.p.A. fornisce
            almeno una comunicazione interlocutoria entro
            <strong>un (1) mese</strong>.
          </p>
          <p class="mb-6">
            L'esercizio dei diritti è, in linea di principio, gratuito;
            UniCredit RE Services S.p.A. si riserva il diritto di chiedere un
            contributo in caso di richieste manifestamente infondate o eccessive
            (anche ripetitive).
          </p>
          <p class="mb-6">
            UniCredit RE Services S.p.A. ha il diritto di chiedere informazioni
            necessarie a fini identificativi del richiedente.
          </p>
        </li>
        <li class="mb-12 marker:font-bold">
          <p class="paragraph mb-6">
            reclamo o segnalazione al garante per la protezione dei dati
            personali
          </p>
          <p class="mb-6">
            UniCredit RE Services S.p.A. La informa che Lei ha diritto di
            proporre reclamo ovvero effettuare una segnalazione al Garante per
            la Protezione dei Dati Personali oppure in alternativa presentare
            ricorso all'Autorità Giudiziaria. I contatti del Garante per la
            Protezione dei Dati Personali sono consultabili sul sito web
            <a
              class="text-blue underline hover:no-underline"
              target="_blank"
              href="http://www.garanteprivacy.it"
            >
              http://www.garanteprivacy.it.
            </a>
          </p>
        </li>
      </ol>
    </div>
  </div>
</template>

<script>
// Components
import Breadcrumbs from '~/components/Breadcrumbs.vue'

export default {
  components: { Breadcrumbs },
  data() {
    return {
      breadcrumbs: [
        {
          text: 'Home',
          href: '/',
        },
        {
          text: 'Privacy',
          href: '/privacy',
        },
        {
          text: 'Informativa privacy per la clientela',
        },
      ],
    }
  },
  head() {
    return {
      title: 'Informativa privacy per la clientela - UniCredit RE Services',
      meta: [
        {
          hid: 'description',
          name: 'description',
          content:
            'Area riservata | Informativa privacy per la clientela - UniCredit RE Services',
        },
      ],
    }
  },
}
</script>
